import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2 } from "lucide-react";

/**
 * Reusable admin password confirmation dialog.
 * Calls onConfirm(password). Parent is responsible for closing on success.
 * Supports an optional `error` prop to surface backend errors.
 */
export default function AdminPasswordDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Confirmer la modification",
  description = "Saisissez votre mot de passe administrateur pour confirmer cette action sensible.",
  confirmLabel = "Confirmer",
  loading = false,
  error = "",
}) {
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!open) setPassword("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="admin-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-emerald-600" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Mot de passe administrateur</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="mt-1 rounded-xl"
            onKeyDown={(e) => { if (e.key === "Enter" && password) onConfirm(password); }}
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="admin-password-cancel">Annuler</Button>
          <Button
            className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl"
            onClick={() => onConfirm(password)}
            disabled={loading || !password}
            data-testid="admin-password-confirm"
          >
            {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Vérification...</>) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
